import { useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { selectActiveCompanyId } from "../../../redux/features/user/selectors/userState.selectors";
import {
  fetchInternalContacts,
  markInternalContactAsRead,
} from "../_queries/internalContacts.queries";
import {
  Contact,
  MarkContactAsReadParams,
  MarkContactAsReadResponse,
} from "../_types/internalContacts.types";
import { getContactId } from "../_utils/internalContacts.utils";

export const INTERNAL_CONTACTS_QUERY_KEY = ["internal-contacts"] as const;

export const getInternalContactsQueryKey = (companyId?: number | string | null) => [
  ...INTERNAL_CONTACTS_QUERY_KEY,
  companyId ?? "no-company",
];


const toMarkAsReadParams = (contact: Contact): MarkContactAsReadParams | null => {
  if (!contact?.type) return null;

  if (contact.type === "company" && contact.id != null) {
    return { contactType: "company", contactId: Number(contact.id) };
  }

  if (contact.type === "workforce") {
    return {
      contactType: "workforce",
      contactUid: contact.uid,
      userUid: contact.userUid,
    };
  }

  if (contact.type === "organizationCoworker") {
    return {
      contactType: "organizationCoworker",
      contactUid: contact.uid,
      userUid: contact.userUid,
    };
  }

  return null;
};


export const useInternalContacts = () => {
  const token = sessionStorage.getItem("token") ?? "";
  const queryClient = useQueryClient();
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const queryKey = useMemo(() => getInternalContactsQueryKey(activeCompanyId), [activeCompanyId]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const payload = await fetchInternalContacts(token);
      return Array.isArray(payload?.items) ? payload.items : [];
    },
    enabled: Boolean(token),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation<MarkContactAsReadResponse, Error, Contact>({
    mutationFn: async (contact) => {
      const params = toMarkAsReadParams(contact);
      if (!params) return { affected: 0 };
      return markInternalContactAsRead(token, params);
    },
    onMutate: async (contact) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Contact[]>(queryKey);
      const contactId = getContactId(contact);

      queryClient.setQueryData<Contact[]>(queryKey, (old) =>
        old?.map((c) =>
          String(getContactId(c)) === String(contactId)
            ? { ...c, unreadCount: 0 }
            : c,
        ),
      );

      return { previous };
    },
    onError: (_err, _contact, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });

  const contacts = useMemo<Contact[]>(() => query.data ?? [], [query.data]);

  const { mutate: markContactAsRead } = mutation;

  const markAsRead = useCallback(
    (contact: Contact) => markContactAsRead(contact),
    [markContactAsRead],
  );

  const totalUnread = useMemo(
    () => contacts.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
    [contacts],
  );

  const getUnreadCount = useCallback(
    (contactId: string | number) => {
      const found = contacts.find((c) => String(getContactId(c)) === String(contactId));
      return found?.unreadCount ?? 0;
    },
    [contacts],
  );

  const incrementUnread = useCallback(
    (contactId: string | number) => {
      queryClient.setQueryData<Contact[]>(queryKey, (old) =>
        old?.map((c) =>
          String(getContactId(c)) === String(contactId)
            ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1 }
            : c,
        ),
      );
    },
    [queryClient, queryKey],
  );

  const updateLastMessage = useCallback(
    (
      contactId: string | number,
      payload: {
        lastMessage?: string | null;
        lastMessageAt?: string | null;
        lastMessageDirection?: "incoming" | "outgoing" | null;
      },
    ) => {
      queryClient.setQueryData<Contact[]>(queryKey, (old) =>
        old?.map((c) =>
          String(getContactId(c)) === String(contactId) ? { ...c, ...payload } : c,
        ),
      );
    },
    [queryClient, queryKey],
  );

  const refetch = useCallback(() => query.refetch(), [query]);

  const contactsRef = useRef(contacts);
  contactsRef.current = contacts;

  return {
    contacts,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch,
    markAsRead,
    isMarkingAsRead: mutation.isPending,
    totalUnread,
    getUnreadCount,
    incrementUnread,
    updateLastMessage,
  };
};
